import type * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';

type AttributeVisibility = 'visible' | 'hidden' | 'unavailable';

type AttributeRowProps = React.ComponentProps<'div'> & {
  label: string;
  value: React.ReactNode;
  /** When set, the row renders an external link and `value` is not rendered. */
  href?: string;
  /** Link text for an `href` row; `value` is not rendered. */
  linkLabel?: string;
  /**
   * When true, the value is an empty-state placeholder (e.g. an em-dash). Adds a
   * visually-hidden "No data" companion so screen readers don't announce a bare dash.
   */
  valueEmpty?: boolean;
  /**
   * Controls the inline eye toggle:
   * - `unavailable` (default): no eye button.
   * - `visible`: eye always shown (feature is on the map).
   * - `hidden`: eye revealed on row hover/focus (feature is off the map).
   */
  visibility?: AttributeVisibility;
  onToggleVisibility?: () => void;
  /** Fires when an `href` row's link is opened by primary or middle click; rows without an `href` ignore it. */
  onLinkClick?: () => void;
  visibilityToggleTestId?: string;
  linkTestId?: string;
  'data-testid'?: string;
};

function AttributeRow({
  label,
  value,
  href,
  linkLabel = 'View record',
  valueEmpty = false,
  visibility = 'unavailable',
  onToggleVisibility,
  onLinkClick,
  visibilityToggleTestId,
  linkTestId,
  className,
  'data-testid': dataTestId,
  ...props
}: AttributeRowProps) {
  const isVisible = visibility === 'visible';

  // Middle-click opens the link through auxclick, which onClick never sees; the button guard drops
  // the secondary button, which fires auxclick too.
  const handleLinkAuxClick = onLinkClick
    ? (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (event.button === 1) onLinkClick();
      }
    : undefined;

  return (
    // min-h-7 plus the eye's fixed h-7 keep rows the same height with or without an eye.
    <div
      {...props}
      data-slot="attribute-row"
      data-testid={dataTestId}
      className={cn(
        'group flex min-h-7 w-full items-center justify-between bg-white px-3 text-xs font-medium',
        className,
      )}
    >
      <div className="flex min-w-0 flex-row items-center gap-2 text-grey-70" title={label}>
        <span className="truncate">{label}</span>
        {visibility !== 'unavailable' && (
          // A real button gives keyboard activation and focus for free.
          <Tooltip text={isVisible ? 'Hide attribute from map' : 'Show attribute on map'} asChild>
            <button
              type="button"
              data-testid={visibilityToggleTestId}
              aria-pressed={isVisible}
              // Name identifies which attribute; on/off state is conveyed by aria-pressed, not by flipping the label.
              aria-label={`Toggle ${label} visibility on map`}
              onClick={onToggleVisibility}
              className={cn(
                'flex h-7 w-[30px] shrink-0 cursor-pointer items-center justify-center text-grey-90 focus-ring',
                isVisible
                  ? 'opacity-100'
                  : 'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
              )}
            >
              <Icon icon={isVisible ? 'eyeOpen' : 'eyeNone'} size={14} />
            </button>
          </Tooltip>
        )}
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          onClick={onLinkClick}
          onAuxClick={handleLinkAuxClick}
          data-testid={linkTestId}
          title={href}
          // Name identifies which attribute; every link row otherwise shares the same constant text.
          aria-label={`${linkLabel}: ${label}`}
          className="shrink-0 whitespace-nowrap text-right text-blue-50 underline hover:text-blue-60 focus-ring"
        >
          {linkLabel}
        </a>
      ) : (
        <span className="min-w-0 break-words text-right text-grey-90">
          {valueEmpty ? (
            <>
              {/* Hide the bare placeholder (e.g. em-dash) from screen readers; announce "No data" instead. */}
              <span aria-hidden="true">{value}</span>
              <span className="sr-only">No data</span>
            </>
          ) : (
            value
          )}
        </span>
      )}
    </div>
  );
}

export { AttributeRow };
export type { AttributeRowProps, AttributeVisibility };
